/* eslint-disable react-hooks/rules-of-hooks */
import { useAtomValue } from 'jotai'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import React, { useCallback, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import styled from '@emotion/styled'
import { PlusCircle } from '@emotion-icons/fa-solid/PlusCircle'
import { Typography } from '@mui/material'
import { Colors, Shapes, Typographies, useTypography } from 'src/components/theme'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { Spinner } from 'src/components/spinner'
import { DarkTooltip } from 'src/components/tooltip/dark-tooltip'
import {
  ChevronRightIcon,
  InsightIconBox,
  InsightIconBoxGoBox,
  InsightIconLink,
  InsightIconText,
} from 'src/components/sidebar-panels/available-insights/insight-icon'
import { CustomInsight } from 'src/types/insight-builder'
import { useResetInsightBuilder } from 'src/views/insight-builder/use-reset-insight-builder'
import { getInsightIcon } from 'src/views/insight-builder/insight-icon-selector'
import { INSIGHT_COLOR } from 'src/atoms/insight-builder'
import { Panel } from 'src/components/sidebar/panel'
import { isLocationSelector } from 'src/atoms/search'
import { useCachedRecoilValue } from 'src/utils/recoil'
import { CustomErrorBoundary } from 'src/components/errors/error-boundary'
import { LockedFeature } from 'src/components/locked-feature'
import { INSIGHTS_TIER } from 'src/constants/insights-tier'
import { COMMANDAI_IDS_MAP } from 'src/constants/command-ai-ids'
import { FlexRow } from 'src/components/flex-containers'

import * as coreAtoms from 'src/v2/state/core'
import * as insightBuilderAtoms from 'src/atoms/insight-builder'

type PersonalInsightItemProps = {
  insight: CustomInsight
  selected: boolean
}
export const PersonalInsightItem = (props: PersonalInsightItemProps) => {
  const { insight, selected } = props
  const router = useRouter()
  const searchEntity = useAtomValue(coreAtoms.searchEntity)

  const icon = getInsightIcon(insight.icon, 16, INSIGHT_COLOR)

  const onClick = useCallback(async () => {
    const queryParams = new URLSearchParams()
    if (searchEntity.ids['am-loc']) {
      queryParams.append('id.am-loc', searchEntity.ids['am-loc'])
    }
    if (searchEntity.ids.google) {
      queryParams.append('id.google', searchEntity.ids.google)
    }
    queryParams.append('iid', insight.id)
    await router.push(`/insights/insight-builder?${queryParams.toString()}`)
  }, [searchEntity])

  // disable tooltip hover on titles that fit in the box (not perfect, but good enough)
  const disableHover = useMemo(() => {
    return insight.title.length < 35
  }, [insight.title])

  return (
    <InsightIconLink onClick={onClick} color={INSIGHT_COLOR} selected={selected} available="true">
      <InsightIconBox color={INSIGHT_COLOR} selected={selected} available="true">
        {icon}
      </InsightIconBox>
      <DarkTooltip title={insight.title} placement="top-start" arrow={false} disableHoverListener={disableHover}>
        <InsightIconText>
          <Typography color={selected ? INSIGHT_COLOR : Colors.neutral500} variant="textmdm">
            {insight.title}
          </Typography>
          <LockedFeature tier="entry" size={Shapes.spacing[4]} />
        </InsightIconText>
      </DarkTooltip>
      <InsightIconBoxGoBox>
        <ChevronRightIcon color={INSIGHT_COLOR} available="true" selected={selected} />
      </InsightIconBoxGoBox>
    </InsightIconLink>
  )
}

const ShowMoreButton = styled(ButtonWithIcon)`
  margin-top: ${Shapes.spacing[2]}px;
  ${useTypography(Typographies.text1m)}
`
const PersonalInsightsList = () => {
  const customInsights = useCachedRecoilValue(insightBuilderAtoms.customInsights, [])
  const selectedInsightId = useRecoilValue(insightBuilderAtoms.selectedCustomInsightId)
  const [showMore, setShowMore] = useState(false)

  const visibleInsights = useMemo(
    () => (showMore ? customInsights : customInsights.slice(0, 4)),
    [showMore, customInsights]
  )

  return (
    <>
      {visibleInsights.length > 0 ? (
        visibleInsights.map((insight) => (
          <PersonalInsightItem key={insight.id} insight={insight} selected={insight.id === selectedInsightId} />
        ))
      ) : (
        <CreateNewPersonalInsightItem />
      )}
      {customInsights.length > 4 && (
        <div style={{ paddingInline: `${Shapes.spacing[5]}px` }}>
          <ShowMoreButton fullWidth onClick={() => setShowMore(!showMore)} ghost>
            {showMore ? 'View Less' : `View ${customInsights.length - visibleInsights.length} More`}
          </ShowMoreButton>
        </div>
      )}
    </>
  )
}

const useCreateNewCustomReport = () => {
  const router = useRouter()
  const searchEntity = useAtomValue(coreAtoms.searchEntity)
  const resetInsightBuilder = useResetInsightBuilder()
  const setIsConfiguring = useSetRecoilState(insightBuilderAtoms.isConfiguring)

  const onClick = async () => {
    const queryParams = new URLSearchParams()
    if (searchEntity.ids['am-loc']) {
      queryParams.append('id.am-loc', searchEntity.ids['am-loc'])
    }
    if (searchEntity.ids.google) {
      queryParams.append('id.google', searchEntity.ids.google)
    }
    await router.push(`/insights/insight-builder?${queryParams.toString()}`)
    resetInsightBuilder()
    setIsConfiguring(true)
  }
  return onClick
}

// Insight tab item when custom reports are empty
const CreateNewPersonalInsightItem = () => {
  const onClick = useCreateNewCustomReport()
  return (
    <InsightIconLink onClick={onClick} color={Colors.blueDark} available="true">
      <InsightIconBox color={Colors.blueDark} available="true" dashed>
        <PlusCircle color={Colors.blueDark} size={12} />
      </InsightIconBox>
      <InsightIconText id={COMMANDAI_IDS_MAP.mapExplorer['create-custom-report-tab']}>
        <Typography color={Colors.blueDark}>Create Report</Typography>
        <LockedFeature tier={INSIGHTS_TIER['Custom Reports']} size={14} />
      </InsightIconText>
      <InsightIconBoxGoBox>
        <ChevronRightIcon color={Colors.blueDark} available="true" selected />
      </InsightIconBoxGoBox>
    </InsightIconLink>
  )
}
// A button
const CreateNewPersonalInsightButton = () => {
  const onClick = useCreateNewCustomReport()
  return (
    <ButtonWithIcon onClick={onClick} color={Colors.blueDark}>
      <PlusCircle color={Colors.blueDark} size={12} />
      Create Report
      <LockedFeature tier={INSIGHTS_TIER['Custom Reports']} size={14} />
    </ButtonWithIcon>
  )
}

const PersonalInsightsLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Shapes.spacing[4]}px;
`

export const PersonalInsights: React.FC = () => {
  const _isLocationSelector = useRecoilValue(isLocationSelector)

  if (_isLocationSelector) {
    return null
  }

  return (
    <CustomErrorBoundary errorDisplay={null}>
      <Panel
        noPadding
        title={
          <div style={{ paddingInline: `${Shapes.spacing[4]}px` }}>
            <FlexRow align="center" justify="space-between">
              <Typography variant="textmd" color={Colors.neutral700}>
                Custom Reports
              </Typography>
              <CreateNewPersonalInsightButton />
            </FlexRow>
          </div>
        }>
        <React.Suspense
          fallback={
            <PersonalInsightsLoading>
              <Spinner size={16} />
            </PersonalInsightsLoading>
          }>
          <PersonalInsightsList />
        </React.Suspense>
      </Panel>
    </CustomErrorBoundary>
  )
}
